import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { serializeAppliedFilters } from 'donor-elf-web/lib/filter-utils-new';
import FundModel from 'donor-elf-web/models/fund';
import moment from 'moment';
import Highcharts from 'highcharts';
import type Settings from 'donor-elf-web/services/settings';

interface MonthlyGiftsChartComponentArgs {
  goal: any;
  fund: FundModel;
  goalAmount?: number; // use this to override the goal amount
  renderChart?: boolean; // default true. If the chart should be rendered
  disclaimerLabel?: string; // optional label for the disclaimer
  disclaimerLink?: string; // optional link for the disclaimer
}

export default class GoalBalanceAndMonthlyGiftsChartComponent extends Component<MonthlyGiftsChartComponentArgs> {
  @service declare formatAmountService: any;
  @service declare settings: Settings;
  @service declare router: RouterService;

  get renderChart() {
    return this.args.renderChart ?? true;
  }

  // has the chart been rendered
  isChartRendered = false;

  get showBalance() {
    return !this.settings.orgSettings!.incomeOnly;
  }

  get goalAmount() {
    return this.args.goalAmount || this.args.goal.goalAmount;
  }

  fontFamily =
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

  get monthlyGifts() {
    return this.args.goal.goalData.monthly_gifts;
  }

  get monthlyGiftsChartCategories() {
    return this.monthlyGifts.map((item: any) => {
      return moment(item.month).format('MMM YY');
    });
  }

  get monthlyGiftsChartGiftsSeries() {
    let { monthlyGifts } = this;
    let series = [];
    for (let i = 0; i < monthlyGifts.length; i++) {
      let amount = monthlyGifts[i].amount;
      series.push({
        y: amount,
        month: monthlyGifts[i].month,
      });
    }
    return series;
  }

  get monthlyGiftsChartGoalSeries() {
    if (this.args.goal.goalType === 'total') {
      return [];
    }
    let { goalAmount, monthlyGifts } = this;
    if (this.args.goal.goalType === 'annual') {
      goalAmount = goalAmount / 12;
    }
    let formattedGoalAmount = this.formatAmountService.format(goalAmount);
    return monthlyGifts.map(function () {
      return {
        y: goalAmount,
        formattedAmount: formattedGoalAmount,
      };
    });
  }

  get monthlyGiftsChartSettings() {
    let self = this;

    return {
      title: {
        text: '',
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        categories: this.monthlyGiftsChartCategories,
        labels: {
          step: 3,
        },
      },
      yAxis: {
        min: 0,
        title: {
          text: null,
        },
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        area: {
          stacking: 'normal',
          marker: {
            enabled: true,
          },
        },
      },
      series: [
        {
          type: 'column',
          name: 'Monthly Gifts',
          color: '#0b68a1',
          data: this.monthlyGiftsChartGiftsSeries,
          tooltip: {
            headerFormat:
              "<span style='font-size: 0.8rem'>{point.key}</span><br/>",
            pointFormatter: function () {
              let formattedAmount = self.formatAmountService.format(
                (this as any).y,
              ) as any;
              return `<span style='font-size=0.8rem'>${formattedAmount}</span>`;
            },
          },
          events: {
            click(event: any) {
              let month = event.point.month;
              self.router.transitionTo('funds.fund.transactions', {
                queryParams: {
                  filters: serializeAppliedFilters([
                    {
                      propertyName: 'transDate',
                      operation: '><',
                      val: [
                        month,
                        moment(month).endOf('month').format('YYYY-MM-DD'),
                      ],
                    },
                    { propertyName: 'income' },
                    { propertyName: 'affectsGoal' },
                  ]),
                },
              });
            },
          },
        },
        {
          type: 'line',
          name: 'Monthly Goal',
          color: '#f0ad4e',
          lineWidth: 3,
          marker: {
            enabled: false,
          },
          data: this.monthlyGiftsChartGoalSeries,
          tooltip: {
            headerFormat:
              "<span style='font-size: 0.8rem'>{point.key}</span><br/>",
            pointFormatter: function (): any {
              return `<span style='font-size=0.8rem'>${(this as any).formattedAmount}</span>`;
            },
          },
        },
      ],
    };
  }

  @action
  handleRenderTheChart(el: any) {
    if (this.renderChart && !this.isChartRendered && !this.isDestroyed) {
      this.isChartRendered = true;
      Highcharts.chart(
        el.querySelector('.monthly-gifts-chart'),
        this.monthlyGiftsChartSettings,
      );
    }
  }
}
